// =============================================================================
// PaymentsPendingPage — finance verification queue
// =============================================================================
// Every payment that finance hasn't yet verified or rejected. Only cash and
// cheque auto-verify at the till, so this is card / Juice / Emtel / MYT /
// bank transfer / other (plus any cheque payments recorded before its
// auto-verify shipped). Two actions per row: Verify (mark verifiedAt — money
// has landed in the bank statement) or Reject (mark rejectedAt + reason —
// money never arrived).
// Rejecting a payment may flip the order's payment-rejected flag AND restock
// any STOCK_AT_LOCATION lines, all server-side.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../lib/api';
import { Card } from '../../components/Card';
import { Money } from '../../components/Money';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { StatusPill } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';
import { cn } from '../../lib/cn';
import { CSV_MIME, downloadBlob } from '../../lib/download';
import { businessTodayIso } from '../../lib/dates';

interface PendingPayment {
  id: string;
  amount: string;
  method: string;
  reference: string | null;
  recordedAt: string;
  isDeliveryCod: boolean;
  recordedBy: { name: string };
  order: {
    id: string;
    number: string;
    total: string;
    status: string;
    paymentOverrideKind: string | null;
    customer: { type: string; name: string; companyName: string | null; phone: string };
    shop: { code: string; name: string };
  };
}

function daysAgo(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function methodTone(method: string): 'info' | 'warning' | 'neutral' {
  if (method === 'cheque') return 'warning';
  if (method === 'juice' || method === 'card' || method === 'bank_transfer') return 'info';
  return 'neutral';
}

export function PaymentsPendingPage(): JSX.Element {
  const qc = useQueryClient();
  const [rejectTarget, setRejectTarget] = useState<PendingPayment | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const pending = useQuery({
    queryKey: ['payments-pending'],
    queryFn: () => apiGet<PendingPayment[]>('/payments/pending'),
    refetchInterval: 60_000,
  });

  const verify = useMutation({
    mutationFn: (id: string) => apiPost(`/payments/${id}/verify`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments-pending'] });
      setErr(null);
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost(`/payments/${id}/reject`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments-pending'] });
      setRejectTarget(null);
      setRejectReason('');
      setErr(null);
    },
    onError: (e) => setErr(getApiError(e).message),
  });

  const rows = pending.data ?? [];
  const totalAmount = rows.reduce((acc, p) => acc + Number(p.amount), 0);

  const downloadCsv = async (): Promise<void> => {
    setDownloading(true);
    setErr(null);
    try {
      await downloadBlob(
        '/payments/pending.csv',
        {},
        CSV_MIME,
        `pending-payments-${businessTodayIso()}.csv`,
      );
    } catch (e) {
      setErr(getApiError(e).message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <PageScaffold
      scroll={false}
      title="Payments pending verification"
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void downloadCsv()}
          loading={downloading}
          disabled={pending.isLoading || pending.isError}
        >
          Download CSV
        </Button>
      }
      subtitle={
        <>
          Payments awaiting reconciliation against the bank statement — only cash and cheque verify
          at the till. Verifying releases the order toward delivery; rejecting voids the payment,
          flags sales, and restocks any reserved goods.
        </>
      }
    >
      <div className="shrink-0 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg bg-ink text-paper-2 p-6">
          <div className="text-[10px] uppercase tracking-wider text-ink-4 font-semibold">
            Awaiting verification
          </div>
          <div className="kc-serif text-5xl text-paper-2 mt-2">{rows.length}</div>
          <div className="text-xs text-ink-4 mt-3">
            payment{rows.length === 1 ? '' : 's'} awaiting verification
          </div>
        </div>
        <div className="rounded-lg bg-surface border border-rule p-6 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Total at stake
          </div>
          <Money value={totalAmount.toFixed(2)} className="kc-serif text-3xl text-ink mt-2 block" />
          <div className="text-xs text-ink-3 mt-2">Sum of pending payments</div>
        </div>
        <div className="rounded-lg bg-surface border border-rule p-6 flex flex-col justify-between">
          <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Oldest
          </div>
          <div className="kc-serif text-3xl text-ink mt-2">
            {rows.length === 0 ? '—' : `${daysAgo(rows[0]!.recordedAt)}d`}
          </div>
          <div className="text-xs text-ink-3 mt-2">since recorded</div>
        </div>
      </div>

      {err && (
        <Card className="shrink-0 border-negative/40 bg-negative-soft">
          <div className="text-sm text-negative">{err}</div>
        </Card>
      )}

      <DataTableCard>
        {pending.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : pending.isError ? (
          <EmptyState
            title="Could not load pending payments"
            description={getApiError(pending.error).message}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing pending"
            description="Every payment needing verification is either verified or rejected. New card / Juice / Emtel / MYT / bank transfer payments will appear here — cash and cheque verify at the till."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-b border-rule">
                <th className="px-6 py-2 font-medium">Invoice</th>
                <th className="px-6 py-2 font-medium">Customer</th>
                <th className="px-6 py-2 font-medium">Shop</th>
                <th className="px-6 py-2 font-medium">Method · ref</th>
                <th className="px-6 py-2 font-medium text-right">Amount</th>
                <th className="px-6 py-2 font-medium">Recorded</th>
                <th className="px-6 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const ageDays = daysAgo(p.recordedAt);
                const stale = ageDays >= 3;
                return (
                  <tr key={p.id} className="border-b border-rule-2 hover:bg-paper-2">
                    <td className="px-6 py-3">
                      <Link
                        to={`/orders/${p.order.id}`}
                        className="kc-mono text-ink hover:text-accent"
                      >
                        {p.order.number}
                      </Link>
                      {p.order.paymentOverrideKind && (
                        <div className="text-[10px] text-ink-3 mt-0.5">
                          override: {p.order.paymentOverrideKind}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-ink">
                        {p.order.customer.type === 'company'
                          ? (p.order.customer.companyName ?? p.order.customer.name)
                          : p.order.customer.name}
                      </div>
                      {p.order.customer.type === 'company' && (
                        <div className="text-xs text-ink-3">Contact: {p.order.customer.name}</div>
                      )}
                      <div className="text-xs text-ink-3">{p.order.customer.phone}</div>
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill>{p.order.shop.code}</StatusPill>
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill tone={methodTone(p.method)}>
                        {p.method.replace('_', ' ')}
                      </StatusPill>
                      {p.reference && (
                        <div className="text-xs text-ink-3 kc-mono mt-0.5">{p.reference}</div>
                      )}
                      {p.isDeliveryCod && (
                        <div className="text-[10px] text-warning mt-0.5">delivery COD</div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Money value={p.amount} className="font-medium" />
                      <div className="text-xs text-ink-3 mt-0.5">
                        of <Money value={p.order.total} muted />
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-ink">
                        {new Date(p.recordedAt).toLocaleDateString('en-MU', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </div>
                      <div
                        className={cn(
                          'text-xs mt-0.5',
                          stale ? 'text-warning font-medium' : 'text-ink-3',
                        )}
                      >
                        {ageDays === 0 ? 'today' : `${ageDays}d ago`}
                      </div>
                      <div className="text-[10px] text-ink-3 mt-0.5">by {p.recordedBy.name}</div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setRejectTarget(p);
                            setRejectReason('');
                          }}
                          disabled={reject.isPending}
                        >
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => verify.mutate(p.id)}
                          loading={verify.isPending && verify.variables === p.id}
                        >
                          Verify
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </DataTableCard>

      {rejectTarget && (
        <Modal
          onClose={() => setRejectTarget(null)}
          title="Reject payment"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRejectTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={() => reject.mutate({ id: rejectTarget.id, reason: rejectReason })}
                loading={reject.isPending}
                disabled={!rejectReason.trim()}
              >
                Reject &amp; void
              </Button>
            </div>
          }
        >
          <div className="space-y-3 text-sm">
            <div className="bg-paper-2 rounded p-3 space-y-1">
              <div className="flex justify-between">
                <span className="text-ink-3">Order</span>
                <span className="kc-mono text-ink">{rejectTarget.order.number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-3">Amount</span>
                <Money value={rejectTarget.amount} className="font-medium" />
              </div>
              <div className="flex justify-between">
                <span className="text-ink-3">Method · ref</span>
                <span>
                  {rejectTarget.method}
                  {rejectTarget.reference ? ` · ${rejectTarget.reference}` : ''}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-3">Recorded by</span>
                <span>{rejectTarget.recordedBy.name}</span>
              </div>
            </div>
            <p className="text-xs text-ink-3">
              Rejecting voids the payment and reverses its GL receipt entry. If the order falls
              below the deposit threshold, stocked goods will be returned to warehouse and the sales
              girl will see a payment-rejected flag on the order.
            </p>
            <Input
              label="Reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="No matching transfer in MCB statement"
              autoFocus
            />
          </div>
        </Modal>
      )}
    </PageScaffold>
  );
}
